import React, { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "./../backendconfig";
import "./DesignerManager.css";

const backend = BACKEND_URL.replace(/\/+$/, "");
const api = `${backend}/designers-manager.php`;

function emptyDesigner(items = []) {
  return {
    id: String(
      items.reduce((max, item) => Math.max(max, Number(item.id) || 0), 0) + 1,
    ),
    name: "",
    image: "",
    experience: "",
    designation: "",
    salary: "",
    salaryPeriod: "/ month",
  };
}

function imageUrl(path) {
  if (!path) return "";

  try {
    const url = new URL(path, `${backend}/`);
    return ["http:", "https:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
}

async function readResponse(response) {
  let data;

  try {
    data = await response.json();
  } catch {
    throw new Error(
      "Invalid API response. Check the PHP file and backend URL.",
    );
  }

  if (!response.ok || !data.ok) {
    throw new Error(data.message || "Unable to process request.");
  }

  return data;
}

function DesignerImage({ src, alt, className = "" }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return <span className="dm-image-empty">Image unavailable</span>;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export default function DesignerManager() {
  const [designers, setDesigners] = useState([]);
  const [version, setVersion] = useState("");
  const [csrf, setCsrf] = useState("");

  const [designer, setDesigner] = useState(() => emptyDesigner());
  const [originalId, setOriginalId] = useState(null);
  const [search, setSearch] = useState("");

  const [upload, setUpload] = useState(null);
  const [uploadPreview, setUploadPreview] = useState("");
  const [removeImage, setRemoveImage] = useState(false);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [photo, setPhoto] = useState(null);

  const fileInput = useRef(null);
  const editor = useRef(null);
  const imageDialog = useRef(null);
  const requestBusy = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      try {
        const response = await fetch(api, {
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        const data = await readResponse(response);

        if (!controller.signal.aborted) {
          setDesigners(data.designers);
          setDesigner(emptyDesigner(data.designers));
          setVersion(data.version);
          setCsrf(data.csrf);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setNotice({ type: "error", text: error.message });
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    load();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!upload) {
      setUploadPreview("");
      return;
    }

    const url = URL.createObjectURL(upload);
    setUploadPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [upload]);

  useEffect(() => {
    if (photo && !imageDialog.current?.open) {
      imageDialog.current?.showModal();
    }
  }, [photo]);

  function resetForm(items = designers) {
    setDesigner(emptyDesigner(items));
    setOriginalId(null);
    setUpload(null);
    setRemoveImage(false);

    if (fileInput.current) fileInput.current.value = "";
  }

  function edit(item) {
    if (requestBusy.current) return;

    setDesigner({ ...item, id: String(item.id) });
    setOriginalId(item.id);
    setUpload(null);
    setRemoveImage(false);
    setNotice(null);

    if (fileInput.current) fileInput.current.value = "";

    editor.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  function updateField(key, value) {
    setDesigner((previous) => ({ ...previous, [key]: value }));
  }

  async function send(action, id = originalId) {
    if (requestBusy.current) return;

    if (action === "delete" && !window.confirm("Delete this designer?")) {
      return;
    }

    if (action === "save") {
      if (
        !Number.isSafeInteger(Number(designer.id)) ||
        Number(designer.id) < 1
      ) {
        setNotice({
          type: "error",
          text: "Designer ID must be a positive whole number.",
        });
        return;
      }

      if (upload && upload.size > 5 * 1024 * 1024) {
        setNotice({
          type: "error",
          text: "Maximum image size is 5 MB.",
        });
        return;
      }
    }

    requestBusy.current = true;
    setBusy(true);
    setNotice(null);

    try {
      const body = new FormData();

      body.append(
        "payload",
        JSON.stringify({
          action,
          originalId: id,
          csrf,
          version,
          removeImage,
          designer: {
            ...designer,
            id: Number(designer.id),
          },
        }),
      );

      if (action === "save" && upload) {
        body.append("image", upload);
      }

      const response = await fetch(api, {
        method: "POST",
        credentials: "include",
        body,
      });

      const data = await readResponse(response);

      setDesigners(data.designers);
      setVersion(data.version);

      if (
        action === "save" ||
        (action === "delete" && String(id) === String(originalId))
      ) {
        resetForm(data.designers);
      } else if (originalId === null) {
        // Keep the default ID current without clearing entered fields.
        setDesigner((previous) => ({
          ...previous,
          id: emptyDesigner(data.designers).id,
        }));
      }

      setNotice({
        type: "success",
        text:
          action === "delete"
            ? "Designer deleted successfully."
            : action === "up" || action === "down"
              ? "Designer order updated."
              : "Designer saved successfully.",
      });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    } finally {
      requestBusy.current = false;
      setBusy(false);
    }
  }

  const preview = upload
    ? uploadPreview
    : removeImage
      ? ""
      : imageUrl(designer.image);

  const query = search.trim().toLowerCase();

  const filtered = designers
    .map((item, index) => ({ item, index }))
    .filter(({ item }) =>
      `${item.id} ${item.name} ${item.designation} ${item.experience}`
        .toLowerCase()
        .includes(query),
    );

  return (
    <main className="dm-page">
      <header className="dm-header">
        <div>
          <h1>Designer Manager</h1>
          <p>Manage designer profiles, experience and salary packages.</p>
        </div>

        <span className="dm-count">{designers.length} designers</span>
      </header>

      {notice && (
        <div
          className={`dm-notice dm-notice-${notice.type}`}
          role={notice.type === "error" ? "alert" : "status"}
        >
          {notice.text}
        </div>
      )}

      {loading ? (
        <section className="dm-panel" role="status">
          Loading designers…
        </section>
      ) : !csrf ? (
        <section className="dm-panel">
          Unable to load the manager. Check the API and reload.
        </section>
      ) : (
        <>
          <section className="dm-panel dm-editor" ref={editor}>
            <h2>{originalId === null ? "Add Designer" : "Edit Designer"}</h2>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                send("save");
              }}
            >
              <fieldset disabled={busy} className="dm-fieldset">
                <div className="dm-grid">
                  <label>
                    Designer ID
                    <input
                      type="number"
                      min="1"
                      step="1"
                      required
                      value={designer.id}
                      onChange={(event) =>
                        updateField("id", event.target.value)
                      }
                    />
                  </label>

                  {[
                    ["name", "Designer Name", "Designer name"],
                    ["experience", "Experience", "3 Years"],
                    ["designation", "Designation", "Embroidery Designer"],
                    ["salary", "Salary", "₹30,000"],
                    ["salaryPeriod", "Salary Period", "/ month"],
                  ].map(([key, label, placeholder]) => (
                    <label key={key}>
                      {label}
                      <input
                        required
                        value={designer[key]}
                        placeholder={placeholder}
                        onChange={(event) =>
                          updateField(key, event.target.value)
                        }
                      />
                    </label>
                  ))}

                  <div className="dm-full dm-upload">
                    <label>
                      Designer Image
                      <input
                        ref={fileInput}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(event) => {
                          setUpload(event.target.files?.[0] || null);
                          setRemoveImage(false);
                        }}
                      />
                    </label>

                    <p className="dm-hint">
                      JPG, PNG, WebP or GIF, up to 5 MB. Leave empty to keep the
                      current image.
                    </p>

                    {preview && (
                      <DesignerImage
                        src={preview}
                        alt="Designer image preview"
                        className="dm-preview"
                      />
                    )}

                    <div className="dm-actions">
                      {preview && (
                        <button
                          type="button"
                          className="dm-danger"
                          onClick={() => {
                            setUpload(null);
                            setRemoveImage(true);
                            if (fileInput.current) {
                              fileInput.current.value = "";
                            }
                          }}
                        >
                          <i className="bi bi-trash" aria-hidden="true" />
                          Remove Image
                        </button>
                      )}

                      {removeImage && designer.image && (
                        <button
                          type="button"
                          onClick={() => setRemoveImage(false)}
                        >
                          Undo Removal
                        </button>
                      )}
                    </div>

                    {removeImage && (
                      <p className="dm-hint">
                        The image will be removed when you save.
                      </p>
                    )}
                  </div>
                </div>

                <div className="dm-actions">
                  <button type="submit" className="dm-primary">
                    <i className="bi bi-check2" aria-hidden="true" />
                    {busy
                      ? "Processing…"
                      : originalId === null
                        ? "Save Designer"
                        : "Update Designer"}
                  </button>

                  <button type="button" onClick={() => resetForm()}>
                    Clear Form
                  </button>
                </div>
              </fieldset>
            </form>

            <p className="dm-hint">
              New designers appear first. Use the arrows to change their order.
            </p>
          </section>

          <section className="dm-panel">
            <div className="dm-toolbar">
              <h2>Our Designers</h2>

              <input
                type="search"
                aria-label="Search designers"
                placeholder="Search name, designation or ID…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className="dm-table-scroll">
              <table className="dm-table">
                <thead>
                  <tr>
                    {[
                      "ID",
                      "Image",
                      "Name",
                      "Experience",
                      "Designation",
                      "Salary",
                      "Salary Period",
                      "Actions",
                    ].map((heading) => (
                      <th key={heading} scope="col">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filtered.map(({ item, index }) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>

                      <td>
                        <button
                          type="button"
                          className="dm-image-button"
                          disabled={!imageUrl(item.image)}
                          aria-label={`View image of ${item.name}`}
                          onClick={() => setPhoto(item)}
                        >
                          <DesignerImage
                            src={imageUrl(item.image)}
                            alt={item.name}
                          />
                        </button>
                      </td>

                      <td>
                        <strong>{item.name}</strong>
                      </td>
                      <td>{item.experience}</td>
                      <td>{item.designation}</td>
                      <td className="dm-salary">{item.salary}</td>
                      <td>{item.salaryPeriod}</td>

                      <td>
                        <div className="dm-row-actions">
                          <button
                            type="button"
                            title="Edit designer"
                            aria-label={`Edit ${item.name}`}
                            disabled={busy}
                            onClick={() => edit(item)}
                          >
                            <i
                              className="bi bi-pencil-square"
                              aria-hidden="true"
                            />
                          </button>

                          <button
                            type="button"
                            title="Move up"
                            aria-label={`Move ${item.name} up`}
                            disabled={busy || index === 0}
                            onClick={() => send("up", item.id)}
                          >
                            <i className="bi bi-arrow-up" aria-hidden="true" />
                          </button>

                          <button
                            type="button"
                            title="Move down"
                            aria-label={`Move ${item.name} down`}
                            disabled={busy || index === designers.length - 1}
                            onClick={() => send("down", item.id)}
                          >
                            <i
                              className="bi bi-arrow-down"
                              aria-hidden="true"
                            />
                          </button>

                          <button
                            type="button"
                            className="dm-danger"
                            title="Delete designer"
                            aria-label={`Delete ${item.name}`}
                            disabled={busy}
                            onClick={() => send("delete", item.id)}
                          >
                            <i className="bi bi-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} className="dm-empty">
                        {designers.length
                          ? "No matching designers."
                          : "No designers yet. Add your first profile."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <dialog
        ref={imageDialog}
        className="dm-dialog"
        aria-labelledby="dm-dialog-title"
        onClose={() => setPhoto(null)}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;

          const rect = event.currentTarget.getBoundingClientRect();

          if (
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom
          ) {
            imageDialog.current?.close();
          }
        }}
      >
        <div className="dm-dialog-heading">
          <h2 id="dm-dialog-title">{photo?.name || "Designer Image"}</h2>
          <button
            type="button"
            aria-label="Close image popup"
            onClick={() => imageDialog.current?.close()}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        {photo && (
          <DesignerImage
            src={imageUrl(photo.image)}
            alt={photo.name}
            className="dm-dialog-image"
          />
        )}
      </dialog>
    </main>
  );
}
